/**
 * @file UndoToast.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description A short message at the foot of the screen with one action
 *              beside it ("Undo") — the pill Pricing & Costing and Rep Pricing
 *              draw for themselves, as a component a screen can drop in.
 *              First used by the Item picker's X (Cam, 30 Oct 2026: an item
 *              taken off "Recently ordered" / "Recently bought" for everyone),
 *              through hooks/usePreviousItemRemoval.
 *
 *              Rendered into document.body, so a dropdown, the grid's
 *              horizontal scroll or a window's overflow never clips it. Goes by
 *              itself — after 9 seconds when it offers an action, 6 otherwise;
 *              hovering keeps it while the pointer is on it. A press on it
 *              never reaches the page behind: an open dropdown (the Item
 *              picker the X was clicked in) stays open through Undo, and keeps
 *              its focus.
 *
 *              props:
 *                toast      { message, tone?: 'info'|'error', actionLabel?,
 *                             onAction?, at } or null (nothing shown). A new
 *                             `at` starts the clock again.
 *                onDismiss  () => void — gone (timed out, closed, or acted on)
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './UndoToast.css';

function UndoToast({ toast, onDismiss }) {
  const [hovered, setHovered] = useState(false);
  const dismissRef = useRef(onDismiss);
  useEffect(() => { dismissRef.current = onDismiss; }, [onDismiss]);

  useEffect(() => {
    if (!toast || hovered) return undefined;
    const ms = toast.onAction ? 9000 : 6000;
    const t = setTimeout(() => dismissRef.current?.(), ms);
    return () => clearTimeout(t);
  }, [toast, hovered]);

  if (!toast || typeof document === 'undefined') return null;
  const tone = toast.tone === 'error' ? 'error' : 'info';
  return createPortal(
    <div
      className={`undo-toast is-${tone}`}
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live="polite"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      <span className="undo-toast-message">{toast.message}</span>
      {toast.onAction && (
        <button
          type="button"
          className="undo-toast-action"
          onClick={() => { const run = toast.onAction; dismissRef.current?.(); run(); }}
        >
          {toast.actionLabel || 'Undo'}
        </button>
      )}
      <button type="button" className="undo-toast-close" aria-label="Close" title="Close" onClick={() => dismissRef.current?.()}>
        <span className="material-symbols-rounded">close</span>
      </button>
    </div>,
    document.body
  );
}

export default UndoToast;
